
import React, { useEffect } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { FontSizeOption } from "@/types/display";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import DisplayCard from "@/components/shared/DisplayCard";

interface CardsViewProps {
  data: {
    items: any[];
    columns?: { key: string; label: string }[];
    title?: string;
    description?: string;
  };
  onRetry?: () => void;
  isHomePage?: boolean;
  showDescriptions?: boolean;
  showColumns?: boolean;
  selectedSections?: string[];
  fontSize?: FontSizeOption;
  displayOptions?: {
    showTitle: boolean;
    showDescription: boolean;
    showItems: boolean;
    showColumns: boolean;
  };
}

const CardsView: React.FC<CardsViewProps> = ({
  data,
  onRetry,
  isHomePage = false,
  showDescriptions = false,
  showColumns = true,
  selectedSections = [],
  fontSize = 'md',
  displayOptions
}) => {
  const isMobile = useIsMobile();

  // Process items based on selected sections
  const processedItems = React.useMemo(() => {
    if (!data?.items) return [];
    
    // If no sections are selected or 'all' is selected, show all items
    if (!selectedSections || selectedSections.length === 0 || selectedSections.includes('all')) {
      return data.items;
    }
    
    // Filter items based on selected sections
    return data.items.filter(item => {
      if (!item.type) return false;
      return selectedSections.includes(item.type);
    });
  }, [data?.items, selectedSections]);

  if (!displayOptions?.showItems || processedItems.length === 0) {
    return (
      <Alert className="mb-10 rounded-2xl">
        <AlertTitle className="text-xl font-bold">אין נתונים להצגה</AlertTitle>
        <AlertDescription>
          <p className="mb-5 text-lg">לא נמצאו פריטים להצגה בתצוגת כרטיסים.</p>
          {onRetry && (
            <Button size="lg" variant="outline" onClick={onRetry} className="flex items-center gap-4 rounded-xl">
              <RefreshCw className="h-8 w-8" />
              <span>רענן נתונים</span>
            </Button>
          )}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="max-w-full mx-auto px-4 py-4 max-h-screen overflow-hidden">
      {!isHomePage && displayOptions?.showTitle && data.title && (
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg p-4 mb-6">
          <h2 className="text-2xl lg:text-3xl font-bold text-center text-gray-900">{data.title}</h2>
        </div>
      )}
      {!isHomePage && displayOptions?.showDescription && data.description && (
        <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 mb-6">
          <p className="text-lg text-center text-gray-700">{data.description}</p>
        </div>
      )}
      
      {/* Responsive grid ensuring all cards fit on screen */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-h-[calc(100vh-200px)] overflow-hidden">
        {processedItems.map((item, index) => (
          <DisplayCard
            key={index}
            title={item.type}
            time={item.title !== item.type ? item.title : undefined}
            description={showDescriptions ? item.chapter : undefined}
            dividerPosition="afterTitle"
          />
        ))}
      </div>
    </div>
  );
};

export default CardsView;
